import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Phone, Mail } from 'lucide-react'
import { getSocialLinks } from '../../config/site'
import { resolveContact } from '../../lib/site'
import { isFeatureEnabled, getDisabledBehavior } from '../../lib/featureFlags'
import type { FlagKey } from '../../config/flags'
import { salons } from '../../config/salons'
import NewsletterForm from '../newsletter/NewsletterForm'

function colVisible(flag: FlagKey): boolean {
  return isFeatureEnabled(flag) || getDisabledBehavior(flag) !== 'hide'
}

export default function FooterV3() {
  const { lang = 'en' } = useParams<{ lang: string }>()
  const { t } = useTranslation()
  // Hardcoded, NOT `new Date().getFullYear()`. A per-render year differs between
  // the prerendered HTML (baked at build) and the client's hydration render when
  // the page is served in a later calendar year than it was built — which caused
  // React hydration error #418 in this copyright line. Bump this each new year.
  const year = 2026
  const withLang = (path: string) => `/${lang}${path}`

  // Contact + social, resolved against the live content.json override (email /
  // instagram / tiktok fall back to siteConfig; phone is opt-in via the admin).
  const contact = resolveContact()
  const social = getSocialLinks().map((s) =>
    s.key === 'instagram'
      ? { ...s, url: contact.instagram }
      : s.key === 'tiktok'
        ? { ...s, url: contact.tiktok }
        : s,
  )

  const showServices = colVisible('SERVICES')
  const showBranches = colVisible('BRANCHES')
  const showAbout = colVisible('ABOUT')
  const showCareers = colVisible('CAREERS')
  const showContact = colVisible('CONTACT')
  const showMembership = colVisible('MEMBERSHIP')
  const showTrichology = colVisible('TRICHOLOGY')
  const showGiftGuide = colVisible('GIFT_GUIDE')
  const showGiftCards = colVisible('GIFT_CARDS')
  const showShop = colVisible('SHOP')

  return (
    <footer className="bg-mocha text-ivory/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:[grid-template-columns:1.2fr_0.8fr_0.9fr_0.8fr_1.5fr] gap-x-6 gap-y-8 sm:gap-8 items-start">
          {/* Col 1 — Brand mark, trust paragraph, email, social.
              Mobile: full-width, first, centred. md+: left-aligned. */}
          <div className="col-span-2 md:col-span-1 order-1 md:order-none text-center md:text-left">
            <Link
              to={withLang('/')}
              className="inline-block mb-3"
              aria-label="Helin Hair"
            >
              <img
                src="/images/brand/logo.avif"
                alt=""
                width={208}
                height={205}
                className="h-10 w-auto"
              />
            </Link>

            <p className="font-body text-sm text-ivory/70 leading-relaxed mb-2">
              {t('footerV3.trustParagraph')}<br />{t('footerV3.trustParagraph2')}
            </p>
            <p className="font-body text-[11px] tracking-[0.2em] text-ivory/40 uppercase mb-3">
              {t('footerV3.trustSignature')} · {t('footerV3.trustEst')}
            </p>

            {contact.phone && (
              <a
                href={`tel:${contact.phone.replace(/\s/g, '')}`}
                className="block font-body text-sm text-ivory/80 hover:text-gold transition-colors mb-4"
              >
                {contact.phone}
              </a>
            )}

            {/* Email + social as a single row of boxed icons (mail matches the
                social-icon treatment). */}
            <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
              <a
                href={`mailto:${contact.email}`}
                aria-label={contact.email}
                className="group h-9 w-9 flex items-center justify-center border border-ivory/15 text-ivory/70 hover:text-gold hover:border-gold/60 transition-colors"
              >
                <span className="sr-only">{contact.email}</span>
                <Mail size={14} strokeWidth={1.75} aria-hidden="true" />
              </a>
              {social.map((s) => (
                <SocialIcon
                  key={s.key}
                  href={s.url}
                  label={t(`components.social.${s.key}`)}
                  icon={s.key}
                />
              ))}
            </div>

          </div>

          {/* Col 2 — Explore (top-level destinations; deep nav via mega-menu).
              Mobile: half-width link group, after the newsletter. */}
          <div className="order-3 md:order-none">
            <ColEyebrow>{t('footerV3.colExplore')}</ColEyebrow>
            <ul className="space-y-2.5">
              {showServices && (
                <li>
                  <FooterLink to={withLang('/services')}>
                    {t('footerV3.allServices')}
                  </FooterLink>
                </li>
              )}
              {showTrichology && (
                <li>
                  <FooterLink to={withLang('/trichology')}>
                    {t('footerV3.allTrichology')}
                  </FooterLink>
                </li>
              )}
              {showMembership && (
                <li>
                  <FooterLink to={withLang('/membership')}>{t('nav.membership')}</FooterLink>
                </li>
              )}
              {showShop && (
                <li>
                  <FooterLink to={withLang('/shop')}>{t('nav.shop')}</FooterLink>
                </li>
              )}
              {showGiftCards && (
                <li>
                  <FooterLink to={withLang('/gift-cards')}>{t('nav.giftCards')}</FooterLink>
                </li>
              )}
              {showGiftGuide && (
                <li>
                  <FooterLink to={withLang('/gift-guide')}>{t('nav.giftGuide')}</FooterLink>
                </li>
              )}
              <li>
                <FooterLink to={withLang('/hair-length-guide')}>
                  {t('footer.links.hairGuide')}
                </FooterLink>
              </li>
              <li>
                <FooterLink to={withLang('/faq')}>{t('footer.links.faq')}</FooterLink>
              </li>
            </ul>
          </div>

          {/* Col 3 — Salons with phone numbers (the missing trust signal) */}
          {showBranches && (
            <div className="order-4 md:order-none">
              <ColEyebrow>{t('footerV3.colSalons')}</ColEyebrow>
              <ul className="space-y-3">
                {salons.map((salon) => (
                  <li key={salon.id}>
                    <FooterLink to={withLang(`/branches#${salon.id}`)}>{salon.name}</FooterLink>
                    <a
                      href={`tel:${salon.phone.replace(/\s/g, '')}`}
                      className="font-body text-sm text-ivory/70 hover:text-gold transition-colors inline-flex items-center gap-1 mt-0.5"
                    >
                      <Phone size={9} aria-hidden="true" />
                      {salon.phone}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Col 4 — Helin (brand links) + Legal sub-group. On mobile the two
              link groups sit side by side (2 sub-columns) so JURIDISK fills the
              empty right cell instead of stacking under HELIN; on md+ they
              recombine into a single stacked column (legal below the brand
              links) to match the wider layout. */}
          <div className="col-span-2 md:col-span-1 order-5 md:order-none grid grid-cols-2 gap-x-6 md:block">
            <div>
              <ColEyebrow>{t('footerV3.colHelin')}</ColEyebrow>
              <ul className="space-y-2.5">
                {showAbout && (
                  <li>
                    <FooterLink to={withLang('/about')}>{t('footer.links.about')}</FooterLink>
                  </li>
                )}
                {showCareers && (
                  <li>
                    <FooterLink to={withLang('/careers')}>{t('footer.links.careers')}</FooterLink>
                  </li>
                )}
                {showContact && (
                  <li>
                    <FooterLink to={withLang('/contact')}>{t('footer.links.contact')}</FooterLink>
                  </li>
                )}
              </ul>
            </div>

            <div>
              {/* Eyebrow: same as ColEyebrow but no top border on mobile (it's a
                  sibling column, not a stacked sub-section); md+ restores the
                  top margin so it reads as a sub-group under the brand links. */}
              <h2 className="font-body font-semibold text-[11px] tracking-[0.25em] text-gold uppercase mb-4 md:mb-3 pb-2 border-b border-gold/20 md:mt-6">
                {t('footerV3.colLegal')}
              </h2>
              <ul className="space-y-2.5">
                <li>
                  <FooterLink to={withLang('/cookies')}>{t('footer.cookies')}</FooterLink>
                </li>
                <li>
                  <FooterLink to={withLang('/privacy')}>{t('footer.privacy')}</FooterLink>
                </li>
                <li>
                  <FooterLink to={withLang('/cancellation-policy')}>
                    {t('footer.cancellation')}
                  </FooterLink>
                </li>
              </ul>
            </div>
          </div>

          {/* Col 5 — Newsletter. Mobile: full-width, lifted to second (right
              under the brand block) so the signup isn't buried below every link
              list, and centred to match the brand block. md: full-width row.
              lg+: inline as the last column, left-aligned. */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1 order-2 md:order-none text-center md:text-left">
            <ColEyebrow>{t('newsletter.heading')}</ColEyebrow>
            <p className="font-body text-sm text-ivory/70 mb-4 leading-relaxed text-justify">
              {t('newsletter.subheading')}
            </p>
            <NewsletterForm variant="footer" />
          </div>
        </div>

        {/* Gold hairline */}
        <div className="mt-8 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

        {/* Bottom bar — local SEO left, copyright centre, legal/CVR right */}
        <div className="mt-3 py-2 grid grid-cols-1 sm:grid-cols-3 items-center gap-1.5 text-center">
          <p className="font-body text-[11px] text-ivory/60 inline-flex items-center justify-center sm:justify-start gap-1.5">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 translate-y-px">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
              <circle cx="12" cy="9" r="2.5"/>
            </svg>
            {t('footerV3.localSeo')}
          </p>
          <p className="font-body text-[11px] text-ivory/60 sm:text-center">
            &copy; {year} Helin Hair. {t('footerV3.rights')}
          </p>
          <p className="font-body text-[11px] text-ivory/60 sm:text-right">
            {t('footerV3.addressLine1')} · {t('footerV3.addressLine2')}
          </p>
        </div>
      </div>
    </footer>
  )
}

function ColEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-body font-semibold text-[11px] tracking-[0.25em] text-gold uppercase mb-4 pb-2 border-b border-gold/20">
      {children}
    </h2>
  )
}

function FooterLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="block font-body text-sm text-ivory/70 hover:text-gold transition-colors"
    >
      {children}
    </Link>
  )
}

type SocialIconProps = {
  href: string
  label: string
  icon: 'instagram' | 'facebook' | 'tiktok' | 'youtube'
}

function SocialIcon({ href, label, icon }: SocialIconProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="group h-9 w-9 flex items-center justify-center border border-ivory/15 text-ivory/70 hover:text-gold hover:border-gold/60 transition-colors"
    >
      <span className="sr-only">{label}</span>
      <Glyph icon={icon} />
    </a>
  )
}

function Glyph({ icon }: { icon: SocialIconProps['icon'] }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  switch (icon) {
    case 'instagram':
      return (
        <svg {...common}>
          <rect x="2.5" y="2.5" width="19" height="19" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      )
    case 'facebook':
      return (
        <svg {...common}>
          <path d="M15 3h-2.5A3.5 3.5 0 0 0 9 6.5V9H6.5v3H9v9h3v-9h2.5l.5-3H12V6.5a.5.5 0 0 1 .5-.5H15V3z" />
        </svg>
      )
    case 'tiktok':
      return (
        <svg {...common}>
          <path d="M14 3v11.5a3.5 3.5 0 1 1-3.5-3.5" />
          <path d="M14 3c.5 2.5 2.5 4.5 5 5" />
        </svg>
      )
    case 'youtube':
      return (
        <svg {...common}>
          <rect x="2.5" y="5.5" width="19" height="13" rx="3" />
          <path d="M10.5 9.5v5l4.5-2.5-4.5-2.5z" fill="currentColor" stroke="none" />
        </svg>
      )
  }
}
